<template>
  <div class="upload-area__timestamp">
    <BasicCheckbox
      value="addTimestamp"
      :modelValue="isEnabled"
      :lineThrough="false"
      :isDisabled="disabled"
      checkType="check"
      class="upload-area__timestamp-checkbox"
      @update:modelValue="onEnabledUpdate"
    >
      <span>Add Timestamp:</span>
    </BasicCheckbox>
    <input
      v-model="timestampInput"
      v-maska="'##:##'"
      type="text"
      class="upload-area__timestamp-input"
      :disabled="!isEnabled || disabled"
      @input="emitChange"
      @focus="onInputFocus"
      @blur="normalizeInput"
      @keydown.enter.prevent.stop="normalizeInput"
    />
  </div>
</template>

<script setup lang="ts">
import { maska as vMaska } from "maska"
import { ref, watch } from "vue"

import BasicCheckbox from "@/components/Form/BasicCheckbox.vue"

import { DEFAULT_TIMESTAMP_VALUE, getTimestampSeconds } from "@/utils/timestampUtil"

const props = withDefaults(
  defineProps<{
    value?: string | number | null
    enabled?: boolean
    disabled?: boolean
    syncEnabled?: boolean
    durationSeconds?: number
  }>(),
  {
    value: DEFAULT_TIMESTAMP_VALUE,
    enabled: true,
    disabled: false,
    syncEnabled: true,
    durationSeconds: undefined
  }
)

const emit = defineEmits<{
  (event: "change", value: number | null, enabled: boolean): void
}>()

const DEFAULT_MASKED_VALUE = "00:00"

const isEnabled = ref(props.enabled && !props.disabled)
const timestampInput = ref(getTimestampInputValue(props.value))
const isFocused = ref(false)

function toMaskedInputValue(seconds: number) {
  const totalSeconds = Math.max(0, Math.floor(seconds))
  const minutes = Math.floor(totalSeconds / 60)
  const restSeconds = totalSeconds % 60

  return `${String(minutes).padStart(2, "0")}:${String(restSeconds).padStart(2, "0")}`
}

function getInputSeconds() {
  const value = timestampInput.value.trim()
  if (!value) return null

  return getSecondsFromValue(value)
}

function getTimestampInputValue(value: string | number | null | undefined) {
  const seconds = getSecondsFromValue(value)

  return seconds === null ? DEFAULT_MASKED_VALUE : toMaskedInputValue(seconds)
}

function getSecondsFromValue(value: string | number | null | undefined) {
  if (typeof value === "number") {
    return getLimitedSeconds(value)
  }

  const trimmedValue = value?.trim()
  if (!trimmedValue) return null

  const seconds = trimmedValue.includes(":")
    ? getTimestampSeconds(trimmedValue)
    : Number(trimmedValue)

  return Number.isFinite(seconds) ? getLimitedSeconds(seconds) : null
}

function getLimitedSeconds(seconds: number) {
  return typeof props.durationSeconds === "number" && Number.isFinite(props.durationSeconds)
    ? Math.min(seconds, Math.max(0, Math.floor(props.durationSeconds) - 1))
    : seconds
}

function normalizeInput() {
  isFocused.value = false
  const seconds = getInputSeconds()
  timestampInput.value = seconds === null ? DEFAULT_MASKED_VALUE : toMaskedInputValue(seconds)
  emitChange()
}

function emitChange() {
  if (!isEnabled.value || props.disabled) {
    emit("change", null, isEnabled.value)
    return
  }

  emit("change", getInputSeconds(), isEnabled.value)
}

function onEnabledUpdate(value: string[] | boolean) {
  if (props.disabled) return

  isEnabled.value = Array.isArray(value) ? value.length > 0 : value
  emitChange()
}

function onInputFocus(event: FocusEvent) {
  isFocused.value = true
  if (event.target instanceof HTMLInputElement) {
    window.setTimeout(() => event.target.select())
  }
}

watch(
  () => props.disabled,
  disabled => {
    if (disabled) {
      isEnabled.value = false
    }
  }
)

watch(
  () => props.enabled,
  enabled => {
    if (!props.syncEnabled) return

    isEnabled.value = enabled && !props.disabled
  }
)

watch(
  () => props.value,
  value => {
    if (isFocused.value) return

    timestampInput.value = getTimestampInputValue(value)
  },
  { immediate: true }
)
</script>
